'use client';

import {
  CardHeader,
  Flex,
  Text,
  Input,
  Button,
  ButtonGroup,
  Column,
  Checkbox,
  DocumentsNavigation,
} from '@/_components';
import { useCallback, useEffect, useContext, useState, type FC } from 'react';
import { searchDocument } from '@/_api/client-documents';
import { useDebounce } from '@/_hooks/useDebounce';
import { DocumentsContext } from '@/_contexts/DocumentsProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { DocumentItem } from '@/_types/models/documents';
import styles from './DocumentsSearch.module.css';
import { ToastContext } from '@/_contexts/ToastProvider';
import type { DocumentsSearchProps } from './DocumentsSearch.types';
import { getIsClientActionsFolderDisabled } from '@/_utils/documents';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { DEFAULT_DEBOUNCE_TIME } from '@/_constants/debounce';
import { getHasPermission } from '@/_utils/permissions';
import { PermissionsMapEnum } from '@/_types/permissions';

export const DocumentsSearch: FC<DocumentsSearchProps> = ({
  companyId,
  isPreview,
}) => {
  const {
    user,
    permissions,
    token,
    isAdvisor,
    isUserReadOnly,
    isCompanyReadOnly,
  } = useContext(UserContext);
  const {
    isLoading: isDocumentsLoading,
    selectedDocuments,
    onShowAllFiles,
    onToggleFolderCreateDialog,
    openDirectory,
    setUploading,
    setSelectedDocuments,
    reloadDocuments,
    searchValue,
    onCopyFolderLocation,
    setSearchValue,
    setOpenDirectory,
    showWithDeleted,
    setShowWithDeleted,
  } = useContext(DocumentsContext);
  const { addToast } = useContext(ToastContext);
  const { preferences } = useContext(FirmSettingsContext);

  const [isLoading, setLoading] = useState<boolean>(false);

  const searchDebounce = useDebounce(searchValue, DEFAULT_DEBOUNCE_TIME);
  const documentCount: number = selectedDocuments?.length ?? 0;

  const isRoot: boolean = openDirectory === '';
  const isReadOnly: boolean = isUserReadOnly || isCompanyReadOnly;

  const shouldDisableClientActions = getIsClientActionsFolderDisabled(
    isAdvisor,
    preferences,
    openDirectory,
  );

  const shouldDisableOnlyFolderActions =
    !isAdvisor && preferences?.disable_folder_action;

  const onOpenFile = () => setUploading(true);

  const onResetState = () => {
    setSearchValue('');
    onShowAllFiles();
  };

  const onSearchChange = (event: any) => {
    const newValue = event.target.value;

    if (!newValue) onResetState();

    setSearchValue(newValue);
  };

  const loadSearchDocument = useCallback(
    async (searchTerm: string) => {
      try {
        setLoading(true);
        const { documents } = await searchDocument({
          companyId: companyId!,
          searchTerm,
          token,
        });

        setSelectedDocuments(documents as Array<DocumentItem>);
      } catch (error) {
        addToast('error', null, { error });
      } finally {
        setLoading(false);
      }
    },
    [companyId, setSelectedDocuments, token, addToast],
  );

  useEffect(() => {
    if (searchDebounce) {
      setOpenDirectory('');

      if (searchDebounce) loadSearchDocument(searchDebounce);
    }
  }, [
    loadSearchDocument,
    searchDebounce,
    openDirectory,
    user?.is_advisor,
    setOpenDirectory,
  ]);

  const onCopyFolderUrl = () => onCopyFolderLocation(openDirectory);

  const isFormDisabled: boolean =
    isDocumentsLoading || isReadOnly || isPreview || isLoading;

  const onKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      loadSearchDocument(event.currentTarget.value);
    }
  };

  const hasAddDocumentPermission: boolean =
    getHasPermission(
      PermissionsMapEnum['document.add'],
      permissions,
      companyId,
    ) || !isAdvisor;

  useEffect(() => {
    if (!isFormDisabled && searchValue) {
      const input = document.getElementById(
        'documents-search-search',
      ) as HTMLInputElement;

      if (input) {
        input.focus();
      }
    }
  }, [isFormDisabled, searchValue]);

  return (
    <CardHeader>
      <Flex isBetween gap={4} isColumn padding={2} isWrapping>
        <Flex isBetween isWrapping>
          <Flex isColumn={true} isWrapping>
            <Text fontWeight="medium">
              {isRoot ? 'Home' : openDirectory.replace(/^\/?/, '')}
            </Text>
            <Text type="small" color="secondary" isEllipsis={true}>
              {documentCount} items
            </Text>
          </Flex>
          {isAdvisor && (
            <Flex gap={2} isYCentered isFullWidth xAlign="end">
              <Checkbox
                label="Include deleted files"
                isChecked={showWithDeleted}
                isDisabled={isLoading || isDocumentsLoading}
                isSmall
                onChange={(e) => {
                  setShowWithDeleted(e.target.checked);
                  reloadDocuments(openDirectory, e.target.checked);
                }}
              />
            </Flex>
          )}
        </Flex>
        <Flex gap={6} yAlign="end" isYCentered isWrapping>
          <Flex gap={6} style={{ marginLeft: 'auto' }} isWrapping xAlign="end">
            <div className={styles.documentsSearch__root}>
              <DocumentsNavigation />
            </div>
            <Column width={280}>
              <Input
                value={searchValue}
                iconRight={!!searchValue ? 'x' : undefined}
                onRightClick={!!searchValue ? onResetState : undefined}
                onChange={onSearchChange}
                onKeyPress={onKeyPress}
                isLoading={isLoading}
                autoComplete="new-password"
                label="Search"
                isDisabled={isFormDisabled}
                placeholder="Search by document or folder name"
                isLabelHidden={true}
                id="documents-search-search"
              />
            </Column>
            <ButtonGroup isSameLine={true}>
              {!!openDirectory && (
                <Button
                  value={
                    isAdvisor ? 'Copy client folder URL' : 'Copy folder URL'
                  }
                  iconLeft="clipboard"
                  isIconOnly={true}
                  withTooltip={true}
                  isDisabled={isFormDisabled}
                  intent="secondary"
                  onClick={onCopyFolderUrl}
                />
              )}
              <Button
                value={
                  shouldDisableClientActions || shouldDisableOnlyFolderActions
                    ? 'Disabled by firm'
                    : 'Add Folder'
                }
                iconLeft="folder-plus"
                isIconOnly={true}
                isDisabled={
                  isFormDisabled ||
                  shouldDisableClientActions ||
                  shouldDisableOnlyFolderActions ||
                  !hasAddDocumentPermission
                }
                withTooltip={true}
                intent="secondary"
                onClick={onToggleFolderCreateDialog}
              />
              <Button
                value="Add"
                isDisabled={
                  isFormDisabled ||
                  shouldDisableClientActions ||
                  !hasAddDocumentPermission
                }
                iconLeft="plus"
                intent="primary"
                tooltip="Disabled by firm"
                withTooltip={shouldDisableClientActions}
                onClick={onOpenFile}
              />
            </ButtonGroup>
          </Flex>
        </Flex>
      </Flex>
    </CardHeader>
  );
};
